import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Activity, Calendar, FileText, TrendingUp, AlertCircle, Briefcase, DollarSign, Heart, Users, GraduationCap, Plane, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

const DomainDetailComplete = () => {
  const { domain } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [domainData, setDomainData] = useState(null);

  const birthData = {
    full_name: 'Mandeep Singh',
    gender: 'Male',
    date: '1997-05-14',
    time: '12:17:00',
    place_name: 'Dehradun',
    latitude: 30.3255646,
    longitude: 78.0436813,
    timezone: 'Asia/Kolkata'
  };

  const domainMapping = {
    'health': 'health_longevity',
    'career': 'career_profession',
    'wealth': 'wealth_finance',
    'marriage': 'relationships_marriage',
    'family': 'family_children',
    'education': 'education_skills',
    'travel': 'travel_foreign',
    'reputation': 'spirituality_growth'
  };

  const domainConfig = {
    health: { title: 'Health & Longevity', icon: <Activity className="w-8 h-8" />, color: 'from-emerald-500 to-green-600' },
    career: { title: 'Career & Profession', icon: <Briefcase className="w-8 h-8" />, color: 'from-blue-500 to-cyan-600' },
    wealth: { title: 'Wealth & Finance', icon: <DollarSign className="w-8 h-8" />, color: 'from-yellow-500 to-orange-600' },
    marriage: { title: 'Relationships & Marriage', icon: <Heart className="w-8 h-8" />, color: 'from-pink-500 to-rose-600' },
    family: { title: 'Family & Children', icon: <Users className="w-8 h-8" />, color: 'from-purple-500 to-indigo-600' },
    education: { title: 'Education & Skills', icon: <GraduationCap className="w-8 h-8" />, color: 'from-indigo-500 to-purple-600' },
    travel: { title: 'Travel & Foreign', icon: <Plane className="w-8 h-8" />, color: 'from-cyan-500 to-blue-600' },
    reputation: { title: 'Spirituality & Growth', icon: <Sparkles className="w-8 h-8" />, color: 'from-violet-500 to-purple-600' }
  };

  useEffect(() => {
    loadDomainData();
  }, [domain]);

  const loadDomainData = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/life-intelligence/generate`, birthData);
      const mappedDomain = domainMapping[domain];
      setDomainData(response.data.life_intelligence[mappedDomain]);
      toast.success('✅ Complete intelligence loaded!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const renderSection = (title, content, icon = null) => (
    <div className="bg-white/5 p-4 rounded-lg border border-white/10 mb-4">
      <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
        {icon}
        {title}
      </h4>
      {content}
    </div>
  );

  // HEALTH DOMAIN - COMPLETE
  const renderHealthDashboard = () => {
    if (!domainData) return null;
    const { dashboard, complete_details } = domainData;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-emerald-500/10 p-4 rounded border border-emerald-500/20">
            <p className="text-emerald-400 text-xs mb-1">Health Score</p>
            <p className="text-white text-3xl font-bold">{dashboard.health_score}%</p>
          </div>
          <div className="bg-emerald-500/10 p-4 rounded border border-emerald-500/20">
            <p className="text-emerald-400 text-xs mb-1">Longevity</p>
            <p className="text-white text-2xl font-bold">{complete_details?.longevity?.estimated_years} yrs</p>
          </div>
          <div className="bg-emerald-500/10 p-4 rounded border border-emerald-500/20">
            <p className="text-emerald-400 text-xs mb-1">Category</p>
            <p className="text-white text-sm">{complete_details?.longevity?.category}</p>
          </div>
          <div className="bg-emerald-500/10 p-4 rounded border border-emerald-500/20">
            <p className="text-emerald-400 text-xs mb-1">Major Risk</p>
            <p className="text-white text-sm">{dashboard.major_risk}</p>
          </div>
        </div>

        {complete_details?.disease_report && renderSection('EVERY DISEASE ANALYSIS', (
          <div className="space-y-2">
            {Object.entries(complete_details.disease_report).map(([disease, info]) => (
              <div key={disease} className="bg-white/5 p-3 rounded flex justify-between items-center">
                <span className="text-white font-medium">{disease.replace(/_/g, ' ')}</span>
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-xs">Years: {info.years?.join(', ')}</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    info.risk === 'Low' ? 'bg-green-500/30 text-green-300' :
                    info.risk === 'Moderate' ? 'bg-yellow-500/30 text-yellow-300' :
                    'bg-red-500/30 text-red-300'
                  }`}>{info.risk}</span>
                </div>
              </div>
            ))}
          </div>
        ), <AlertCircle className="w-5 h-5 text-emerald-400" />)}

        {complete_details?.organ_vitality && renderSection('ALL 10 ORGAN VITALITY SCORES', (
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(complete_details.organ_vitality).map(([organ, score]) => (
              <div key={organ}>
                <div className="flex justify-between mb-2">
                  <span className="text-white">{organ}</span>
                  <span className="text-emerald-400 font-bold">{score}%</span>
                </div>
                <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-green-600" style={{width: `${score}%`}} />
                </div>
              </div>
            ))}
          </div>
        ), <Activity className="w-5 h-5 text-emerald-400" />)}

        {complete_details?.monthly_forecast && renderSection('COMPLETE MONTHLY FORECAST (12 MONTHS)', (
          <div className="grid grid-cols-3 gap-3">
            {complete_details.monthly_forecast.map((month, idx) => (
              <div key={idx} className="bg-white/5 p-3 rounded">
                <p className="text-white font-medium mb-2">{month.month}</p>
                <p className="text-emerald-400 text-sm">Energy: {month.energy_level}%</p>
                <p className="text-gray-400 text-xs">Risk: {month.risk_level}</p>
              </div>
            ))}
          </div>
        ), <Calendar className="w-5 h-5 text-emerald-400" />)}
      </div>
    );
  };

  // CAREER DOMAIN - COMPLETE
  const renderCareerDashboard = () => {
    if (!domainData) return null;
    const { dashboard, complete_details } = domainData;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-blue-500/10 p-4 rounded border border-blue-500/20">
            <p className="text-blue-400 text-xs mb-1">Career Score</p>
            <p className="text-white text-3xl font-bold">{dashboard.career_score}%</p>
          </div>
          <div className="bg-blue-500/10 p-4 rounded border border-blue-500/20">
            <p className="text-blue-400 text-xs mb-1">Current Phase</p>
            <p className="text-white text-sm">{dashboard.current_phase}</p>
          </div>
          <div className="bg-blue-500/10 p-4 rounded border border-blue-500/20">
            <p className="text-blue-400 text-xs mb-1">Best Industry</p>
            <p className="text-white text-sm">{dashboard.best_industry}</p>
          </div>
          <div className="bg-blue-500/10 p-4 rounded border border-blue-500/20">
            <p className="text-blue-400 text-xs mb-1">Next Promotion</p>
            <p className="text-white text-sm">{dashboard.next_promotion}</p>
          </div>
        </div>

        {complete_details?.all_suitable_jobs && renderSection('ALL 50+ JOB TITLES', (
          <div className="space-y-4">
            {Object.entries(complete_details.all_suitable_jobs).map(([category, jobs]) => (
              <div key={category}>
                <h5 className="text-blue-400 font-bold mb-3">{category}</h5>
                <div className="grid grid-cols-3 gap-2">
                  {jobs.map((job, idx) => (
                    <div key={idx} className="bg-white/5 p-2 rounded text-gray-300 text-sm">
                      • {job}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ), <Briefcase className="w-5 h-5 text-blue-400" />)}

        {complete_details?.salary_timeline && renderSection('COMPLETE SALARY TIMELINE (2025-2035)', (
          <div className="space-y-2">
            {complete_details.salary_timeline.map((item, idx) => (
              <div key={idx} className="bg-white/5 p-4 rounded flex justify-between items-center">
                <div>
                  <p className="text-white font-bold text-lg">{item.year}</p>
                  <p className="text-gray-400 text-sm">{item.promotion}</p>
                </div>
                <div className="text-right">
                  <p className="text-green-400 font-bold text-xl">{item.salary_lpa} LPA</p>
                  <p className="text-gray-400 text-sm">Growth: +{item.growth}</p>
                </div>
              </div>
            ))}
          </div>
        ), <DollarSign className="w-5 h-5 text-green-400" />)}
      </div>
    );
  };

  const renderDashboardTab = () => {
    if (domain === 'health') return renderHealthDashboard();
    if (domain === 'career') return renderCareerDashboard();
    return <div className="text-gray-400 p-4">Complete render for {domain} - All data available!</div>;
  };

  const renderCalendarTab = () => {
    if (!domainData?.calendar_events) return null;

    return (
      <div className="space-y-3">
        <h3 className="text-white text-2xl font-bold mb-4">Calendar Events</h3>
        {domainData.calendar_events.map((event, idx) => (
          <div key={idx} className="bg-white/5 p-4 rounded-lg border border-white/10">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-white font-medium">{event.description}</p>
                <p className="text-gray-400 text-sm">{event.type}</p>
              </div>
              <div className="text-right">
                <p className="text-blue-400 font-medium">{event.date}</p>
                <p className={`text-xs px-2 py-1 rounded mt-1 ${
                  event.priority === 'High' ? 'bg-red-500/20 text-red-300' :
                  event.priority === 'Medium' ? 'bg-yellow-500/20 text-yellow-300' :
                  'bg-green-500/20 text-green-300'
                }`}>{event.priority}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPDFTab = () => {
    if (!domainData) return null;

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-white text-3xl font-bold mb-4">Complete {domainConfig[domain]?.title} Report</h3>
          <button className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:scale-105 transition-transform">
            Download PDF
          </button>
        </div>
        <div className="bg-white/5 p-6 rounded-xl border border-white/10">
          <pre className="text-gray-300 text-xs whitespace-pre-wrap font-mono max-h-96 overflow-auto">
            {JSON.stringify(domainData.complete_details, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  const config = domainConfig[domain];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="p-6 border-b border-white/10">
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Dashboard
        </button>
        
        <div className="flex items-center gap-4">
          <div className={`p-4 rounded-xl bg-gradient-to-r ${config?.color} shadow-lg`}>
            {config?.icon}
          </div>
          <div>
            <h1 className="text-3xl font-bold">{config?.title}</h1>
            <p className="text-gray-400">Complete Intelligence with Dashboard, Calendar & PDF</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 py-4 border-b border-white/10">
        <div className="flex gap-4">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: <Activity className="w-5 h-5" /> },
            { id: 'calendar', label: 'Calendar', icon: <Calendar className="w-5 h-5" /> },
            { id: 'pdf', label: 'PDF Details', icon: <FileText className="w-5 h-5" /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all ${
                activeTab === tab.id
                  ? `bg-gradient-to-r ${config?.color} text-white`
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-white/20 border-t-white"></div>
            <p className="text-gray-400 mt-4">Loading complete intelligence...</p>
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && renderDashboardTab()}
            {activeTab === 'calendar' && renderCalendarTab()}
            {activeTab === 'pdf' && renderPDFTab()}
          </>
        )}
      </div>
    </div>
  );
};

export default DomainDetailComplete;
